import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Stack } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import {
  faCheckCircle,
  faCirclePlay,
  faClose,
  faInfoCircle,
  faPlusCircle,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import moment from 'moment-timezone';

import CustomInputGroupWithIcon from 'src/components/custom_components/customInputGroupWithIcon';
import CustomTable from 'src/components/custom_components/CustomTable/CustomTable';

import { Api } from 'src/models/Api.model';
import { EntityCriteriaOperatorEnum } from 'src/models/GalenData/EntityCriteria.model';
import { PageOfUser } from 'src/models/GalenData/PageOfUser.model';
import { IGetUsersRequestBody } from 'src/models/GalenData/Request.model';
import { SortOrderEnum } from 'src/models/GalenData/SortOrder.model';
import { GalenDataUserColumn } from 'src/models/GalenData/User.model';
import { UserRoleEnum } from 'src/models/GalenData/UserRole.model';
import { getLocale, getLocaleCode, Language } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

import {
  namePattern,
  pidPattern,
  validateDatePattern,
  validateNameLength,
  validateNamePattern,
  validatePID,
  validatePIDLength,
} from 'src/utils/validationRules';

import { SearchPatientFormValues } from '../NewPatientHeader/NewPatientHeader';

const DEFAULT_PAGE_SIZE = 10;

export type SearchPatientInfoFormGroupProps = {
  setShowConfirmModal: (show: boolean) => void;
  setIsNewPatientModalOpen: (show: boolean) => void;
  isCreatePatientEnabled: boolean;
  setShowDrawer: (show: boolean) => void;
  setFormValues: (values: Partial<Session>) => void;
  searchMethods: UseFormReturn<SearchPatientFormValues>;
};

export default function SearchPatientInfoFormGroup({
  setShowConfirmModal,
  setIsNewPatientModalOpen,
  isCreatePatientEnabled,
  setShowDrawer,
  setFormValues,
  searchMethods,
}: SearchPatientInfoFormGroupProps) {
  const { tCommon, tPages, language } = useUiTranslation();

  const {
    register,
    control,
    setError,
    clearErrors,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = searchMethods;

  const ValidFieldIcon = (field: keyof SearchPatientFormValues) => {
    return (
      getValues(field) &&
      !errors[field] && (
        <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
      )
    );
  };

  const registerDOB = {
    ...register('dob', {
      required: tCommon('patientFields.dobRequired'),
    }),
    ref: undefined,
  };

  const [patientsData, setPatientsData] = useState<PageOfUser>();

  const [isGettingPatients, setIsGettingPatients] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const getAllUsers = useCallback(
    async (pageNumber: number, pageSize: number) => {
      try {
        setIsGettingPatients(true);

        if (Object.keys(errors).length > 0) {
          return;
        }

        const { firstName, lastName, id, dob } = getValues();

        const filterConditions: {
          key: keyof GalenDataUserColumn | keyof IGetUsersRequestBody;
          value: string | undefined;
          operator: EntityCriteriaOperatorEnum;
        }[] = [
          {
            key: 'patientId',
            value: id?.trim(),
            operator: EntityCriteriaOperatorEnum.Like,
          },
          {
            key: 'dateOfBirth',
            value: dob ? moment(dob).format('YYYY-MM-DD') : undefined,
            operator: EntityCriteriaOperatorEnum.Equal,
          },
          {
            key: 'firstName',
            value: firstName?.trim(),
            operator: EntityCriteriaOperatorEnum.Like,
          },
          {
            key: 'lastName',
            value: lastName?.trim(),
            operator: EntityCriteriaOperatorEnum.Like,
          },
          {
            key: 'role',
            value: UserRoleEnum.Patient,
            operator: EntityCriteriaOperatorEnum.Equal,
          },
        ];

        const filters = filterConditions
          .filter(({ value }) => value)
          .map(({ key, value, operator }) => ({
            key,
            value,
            operator,
            isCustomField: false,
          }));

        const data = await Api.remote._get_users_advanced(filters, {
          pageNumber,
          pageSize,
          sortBy: ['createdOn'],
          sortOrder: SortOrderEnum.DESC,
        });

        setPatientsData({
          content: data.data.content,
          totalElements: data.data.totalElements,
          totalPages: data.data.totalPages,
        });
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
      } finally {
        setIsGettingPatients(false);
      }
    },
    [errors, getValues, handleHttpRequestError, language, tCommon],
  );

  useEffect(() => {
    getAllUsers(0, DEFAULT_PAGE_SIZE);

    setCurrentPage(1);
  }, [getAllUsers]);

  const debouncedGetAllUsers = useMemo(
    () =>
      debounce(() => {
        getAllUsers(0, DEFAULT_PAGE_SIZE);
        setCurrentPage(1);
      }, 1000),
    [getAllUsers],
  );

  const hasAnyValue = () => {
    const values = getValues();
    return Boolean(
      values.firstName || values.lastName || values.id || values.dob,
    );
  };

  const handleClear = () => {
    reset({
      dob: '',
      firstName: '',
      lastName: '',
      id: '',
    });
    setCurrentPage(1);
    getAllUsers(0, DEFAULT_PAGE_SIZE);
  };
  const history = useHistory();

  return (
    <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
      <Stack direction="horizontal" gap={3} className="mb-3">
        <Stack
          direction="horizontal"
          gap={2}
          className="align-items-start flex-fill"
        >
          <Form.Group controlId="dob" className="flex-fill">
            <Form.Label className="d-flex align-items-center">
              {tCommon('patientFields.DOB')} ({tCommon('dateFormat')}){' '}
              {ValidFieldIcon('dob')}
            </Form.Label>

            <Controller
              control={control}
              {...registerDOB}
              render={({ field: { onChange, value } }) => (
                <div className="position-relative z-3">
                  <CustomInputGroupWithIcon>
                    <DatePicker
                      locale={getLocale(language as Language)}
                      id="dob"
                      showIcon={false}
                      autoComplete="off"
                      wrapperClassName="flex-grow-1"
                      popperClassName="custom-datepicker-popper"
                      calendarClassName="custom-datepicker"
                      className="form-control rounded-start-0"
                      placeholderText={tCommon('dateFormat')}
                      dateFormat={['yyyy-MM-dd', 'yyyy/MM/dd', 'yyyy.MM.dd']}
                      maxDate={new Date()}
                      showMonthDropdown
                      showYearDropdown
                      yearDropdownItemNumber={150}
                      scrollableYearDropdown
                      onChange={(date, e) => {
                        const target = e?.target as HTMLInputElement;
                        if (
                          !target.value ||
                          validateDatePattern(target.value)
                        ) {
                          setValue(
                            'dob',
                            moment(value).toDate().toDateString(),
                            {
                              shouldDirty: true,
                            },
                          );
                          onChange(date);
                          debouncedGetAllUsers();
                        }
                      }}
                      selected={value ? moment(value).toDate() : undefined}
                    />
                  </CustomInputGroupWithIcon>
                </div>
              )}
            />
            {errors.dob?.message && (
              <p className="text-danger">{errors.dob?.message}</p>
            )}
          </Form.Group>

          <Form.Group as={Col} controlId="firstName" className="flex-fill">
            <Form.Label>
              {tCommon('patientFields.First Name')}
              {ValidFieldIcon('firstName')}
            </Form.Label>
            <CustomInputGroupWithIcon>
              <Form.Control
                placeholder={tCommon('patientFields.firstNamePlaceholder')}
                {...register('firstName', {
                  required: tCommon('patientFields.firstNameRequired'),
                  maxLength: {
                    value: 100,
                    message: tCommon('patientFields.firstNameMaxLength'),
                  },
                  pattern: {
                    value: namePattern,
                    message: tCommon('patientFields.firstNameInvalid'),
                  },
                })}
                onChange={(e) => {
                  if (e.target.value) {
                    if (!validateNamePattern(e.target.value)) {
                      setError('firstName', {
                        type: 'pattern',
                        message: tCommon('patientFields.firstNameInvalid'),
                      });
                    } else if (!validateNameLength(e.target.value)) {
                      setError('firstName', {
                        type: 'maxLength',
                        message: tCommon('patientFields.firstNameMaxLength'),
                      });
                    } else {
                      if (errors.firstName) {
                        clearErrors('firstName');
                      }
                    }
                  } else {
                    clearErrors('firstName');
                  }

                  setValue('firstName', e.target.value, {
                    shouldDirty: true,
                  });
                  debouncedGetAllUsers();
                }}
                autoComplete="off"
              />
            </CustomInputGroupWithIcon>

            {errors.firstName?.message && (
              <p className="text-danger">{errors.firstName?.message}</p>
            )}
          </Form.Group>

          <Form.Group as={Col} controlId="lastName" className="flex-fill">
            <Form.Label>
              {tCommon('patientFields.Last Name')} {ValidFieldIcon('lastName')}
            </Form.Label>
            <CustomInputGroupWithIcon>
              <Form.Control
                placeholder={tCommon('patientFields.lastNamePlaceholder')}
                {...register('lastName', {
                  required: tCommon('patientFields.lastNameRequired'),
                  maxLength: {
                    value: 100,
                    message: tCommon('patientFields.lastNameMaxLength'),
                  },
                  pattern: {
                    value: namePattern,
                    message: tCommon('patientFields.lastNameInvalid'),
                  },
                })}
                onChange={(e) => {
                  if (e.target.value) {
                    if (!validateNamePattern(e.target.value)) {
                      setError('lastName', {
                        type: 'pattern',
                        message: tCommon('patientFields.lastNameInvalid'),
                      });
                    } else if (!validateNameLength(e.target.value)) {
                      setError('lastName', {
                        type: 'maxLength',
                        message: tCommon('patientFields.lastNameMaxLength'),
                      });
                    } else {
                      if (errors.lastName) {
                        clearErrors('lastName');
                      }
                    }
                  } else {
                    clearErrors('lastName');
                  }

                  setValue('lastName', e.target.value, { shouldDirty: true });
                  debouncedGetAllUsers();
                }}
                autoComplete="off"
              />
            </CustomInputGroupWithIcon>

            {errors.lastName?.message && (
              <p className="text-danger">{errors.lastName?.message}</p>
            )}
          </Form.Group>

          <Form.Group as={Col} controlId="id" className="flex-fill">
            <Form.Label>
              {tCommon('patientFields.PID')}
              {ValidFieldIcon('id')}
            </Form.Label>

            <CustomInputGroupWithIcon>
              <Form.Control
                type="text"
                placeholder={tCommon('patientFields.pidPlaceholder')}
                autoComplete="off"
                {...register('id', {
                  required: tCommon('patientFields.pidRequired'),
                  maxLength: {
                    value: 20,
                    message: tCommon('patientFields.pidMaxLength'),
                  },
                  pattern: {
                    value: pidPattern,
                    message: tCommon('patientFields.pidInvalid'),
                  },
                })}
                onChange={(e) => {
                  if (e.target.value) {
                    if (!validatePIDLength(e.target.value)) {
                      setError('id', {
                        type: 'maxLength',
                        message: tCommon('patientFields.pidMaxLength'),
                      });
                    } else if (!validatePID(e.target.value)) {
                      setError('id', {
                        type: 'pattern',
                        message: tCommon('patientFields.pidInvalid'),
                      });
                    } else {
                      if (errors.id) {
                        clearErrors('id');
                      }
                    }
                  } else {
                    clearErrors('id');
                  }

                  setValue('id', e.target.value, { shouldDirty: true });
                  debouncedGetAllUsers();
                }}
              />
            </CustomInputGroupWithIcon>

            {errors.id?.message && (
              <p className="text-danger">{errors.id?.message}</p>
            )}
          </Form.Group>

          <Button
            variant="outline-warning"
            className="d-flex"
            onClick={handleClear}
            disabled={!hasAnyValue()}
            style={{ marginTop: '32px' }}
          >
            <FontAwesomeIcon icon={faClose} />
          </Button>
        </Stack>
      </Stack>

      <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
        <div className="flex-grow-1" style={{ minHeight: 0 }}>
          <CustomTable
            isLoading={isGettingPatients}
            dataSource={patientsData?.content || []}
            rowKey="userId"
            pagination={{
              defaultPageSize: DEFAULT_PAGE_SIZE,
              pageSizeOptions: [DEFAULT_PAGE_SIZE],
              totalPages: patientsData?.totalPages ?? 0,
              currentPage: currentPage,
              handleSetCurrentPage: setCurrentPage,
              onChangePage: (page, pageSize) => {
                getAllUsers(page - 1, pageSize);
              },
            }}
            columns={[
              {
                title: tCommon('patientFields.DOB'),
                key: 'dateOfBirth',
              },
              {
                title: tCommon('patientFields.First Name'),
                key: 'firstName',
              },
              {
                title: tCommon('patientFields.Last Name'),
                key: 'lastName',
              },
              {
                title: tCommon('patientFields.PID'),
                key: 'patientId',
              },
              {
                title: tCommon('orderFields.actions'),
                dataIndex: 'actions',
                render: (data) => {
                  if (!data) {
                    return;
                  }

                  return (
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        className="rounded-3 px-3 py-1"
                        onClick={() => {
                          setFormValues({
                            firstName: data.firstName,
                            lastName: data.lastName,
                            id: data.patientId ?? '',
                            dob: data.dateOfBirth,
                            patientUserId: data.userId,
                          });
                          setShowConfirmModal(true);
                        }}
                        disabled={!isCreatePatientEnabled}
                      >
                        <FontAwesomeIcon icon={faCirclePlay} />
                      </Button>
                      <Button
                        variant="outline-primary"
                        className="rounded-3 px-3 py-1"
                        onClick={() => {
                          setFormValues({
                            firstName: data.firstName,
                            lastName: data.lastName,
                            id: data.patientId ?? '',
                            dob: data.dateOfBirth,
                            age: moment().diff(data.dateOfBirth, 'years'),
                            patientUserId: data.userId,
                          });
                          setShowDrawer(true);
                        }}
                        data-test="select-patient-button"
                      >
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </Button>
                    </div>
                  );
                },
              },
            ]}
            empty={{
              buttons: [
                {
                  icon: <FontAwesomeIcon icon={faSearch} className="me-2" />,
                  text: tPages('newExam.searchResults'),
                  onClick: () => history.push('/results'),
                  variant: 'primary-100',
                },
                {
                  icon: (
                    <FontAwesomeIcon icon={faPlusCircle} className="me-2" />
                  ),
                  text: tPages('newExam.createNewPatient'),
                  onClick: () => setIsNewPatientModalOpen(true),
                  variant: 'primary',
                },
              ],
            }}
          />
        </div>
      </div>
    </div>
  );
}
